import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Trash2, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLE = {
  Draft: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300',
  Incomplete: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
  Complete: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const BulkAssessmentsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [squadFilter, setSquadFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);

  // Create-form state
  const [categories, setCategories] = useState({});
  const [athletes, setAthletes] = useState([]);
  const [form, setForm] = useState({
    session_name: '',
    assessment_date: new Date().toISOString().split('T')[0],
    season: '',
    squad: '',
    athlete_ids: [],
    test_types: [],
    notes: '',
  });

  useEffect(() => {
    if (!['Admin', 'Staff'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    fetchList();
    fetchFormData();
  }, [user, navigate]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (squadFilter !== 'all') params.squad = squadFilter;
      if (search.trim()) params.q = search.trim();
      const res = await axios.get(`${API}/bulk-assessments`, { params });
      setSessions(res.data || []);
    } catch {
      toast.error('Failed to load bulk sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormData = async () => {
    try {
      const [catsRes, athsRes] = await Promise.all([
        axios.get(`${API}/assessments/categories`),
        axios.get(`${API}/athletes`),
      ]);
      setCategories(catsRes.data);
      setAthletes(athsRes.data || []);
    } catch {
      /* handled via sessions load */
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, squadFilter]);

  const squads = Array.from(
    new Set(athletes.map(a => a.squad).filter(Boolean))
  ).sort();

  const filteredAthletes = form.squad
    ? athletes.filter(a => a.squad === form.squad)
    : athletes;

  const canonicalCategories = Object.entries(categories).filter(
    ([k]) => !k.startsWith('_')
  );

  const toggleAthlete = (id) => {
    setForm(prev => ({
      ...prev,
      athlete_ids: prev.athlete_ids.includes(id)
        ? prev.athlete_ids.filter(a => a !== id)
        : [...prev.athlete_ids, id],
    }));
  };

  const toggleTest = (test) => {
    setForm(prev => ({
      ...prev,
      test_types: prev.test_types.includes(test)
        ? prev.test_types.filter(t => t !== test)
        : [...prev.test_types, test],
    }));
  };

  const selectAllSquadAthletes = () => {
    setForm(prev => ({
      ...prev,
      athlete_ids: Array.from(new Set([
        ...prev.athlete_ids,
        ...filteredAthletes.map(a => a.id),
      ])),
    }));
  };

  const clearAthletes = () => setForm(prev => ({ ...prev, athlete_ids: [] }));

  const createSession = async () => {
    if (!form.session_name.trim()) return toast.error('Name is required');
    if (form.athlete_ids.length === 0) return toast.error('Pick at least one athlete');
    if (form.test_types.length === 0) return toast.error('Pick at least one test');
    try {
      const res = await axios.post(`${API}/bulk-assessments`, form);
      toast.success('Session created');
      setCreateOpen(false);
      setForm({
        session_name: '', assessment_date: new Date().toISOString().split('T')[0],
        season: '', squad: '', athlete_ids: [], test_types: [], notes: '',
      });
      navigate(`/assessments/bulk/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create session');
    }
  };

  const deleteSession = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This removes all linked results.`)) return;
    try {
      await axios.delete(`${API}/bulk-assessments/${id}`);
      toast.success('Deleted');
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6 flex justify-between items-start gap-4 flex-wrap">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bulk Assessment Sessions
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Run testing days across a squad with one entry grid.
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-pfap-green hover:bg-pfap-green/90"
            data-testid="new-bulk-session-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New Bulk Session
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="pt-6 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-gray-500">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') fetchList(); }}
                  placeholder="Search session name…"
                  className="pl-8"
                  data-testid="bulk-search"
                />
              </div>
            </div>
            <div className="w-40">
              <Label className="text-xs text-gray-500">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="bulk-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Incomplete">Incomplete</SelectItem>
                  <SelectItem value="Complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs text-gray-500">Squad</Label>
              <Select value={squadFilter} onValueChange={setSquadFilter}>
                <SelectTrigger data-testid="bulk-filter-squad"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All squads</SelectItem>
                  {squads.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchList} data-testid="bulk-apply-search">Apply</Button>
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Assessment Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8"
                 data-testid="bulk-empty">
                No bulk sessions yet. Create your first one above.
              </p>
            ) : (
              <div className="space-y-2" data-testid="bulk-session-list">
                {sessions.map(s => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/40 cursor-pointer transition"
                    onClick={() => navigate(`/assessments/bulk/${s.id}`)}
                    data-testid={`bulk-row-${s.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {s.session_name}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_STYLE[s.status] || STATUS_STYLE.Draft}`}>
                          {s.status}
                        </span>
                        {s.squad && (
                          <Badge variant="outline" className="text-[10px]">{s.squad}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-3 flex-wrap">
                        <span>{s.assessment_date}</span>
                        <span>{s.athlete_count} athlete{s.athlete_count !== 1 && 's'}</span>
                        <span>{s.test_count} test{s.test_count !== 1 && 's'}</span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {s.completion_percent}% complete
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(user?.role === 'Admin' || s.created_by === user?.id) && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={(e) => { e.stopPropagation(); deleteSession(s.id, s.session_name); }}
                          data-testid={`bulk-delete-${s.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-create-dialog">
          <DialogHeader>
            <DialogTitle>New Bulk Assessment Session</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Session name *</Label>
                <Input
                  value={form.session_name}
                  onChange={e => setForm(p => ({ ...p, session_name: e.target.value }))}
                  placeholder="Fitness Testing — U17 — 29/04/2026"
                  data-testid="bulk-name"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.assessment_date}
                  onChange={e => setForm(p => ({ ...p, assessment_date: e.target.value }))}
                  data-testid="bulk-date"
                />
              </div>
              <div>
                <Label>Season</Label>
                <Input
                  value={form.season}
                  onChange={e => setForm(p => ({ ...p, season: e.target.value }))}
                  placeholder="2025-2026"
                  data-testid="bulk-season"
                />
              </div>
              <div>
                <Label>Squad</Label>
                <Select
                  value={form.squad || 'any'}
                  onValueChange={v => setForm(p => ({ ...p, squad: v === 'any' ? '' : v }))}
                >
                  <SelectTrigger data-testid="bulk-squad"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {squads.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Athletes * ({form.athlete_ids.length} selected)</Label>
                <div className="flex gap-2">
                  {form.squad && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={selectAllSquadAthletes}
                      data-testid="bulk-select-all-squad"
                    >
                      Select all in {form.squad}
                    </Button>
                  )}
                  <Button
                    type="button" variant="ghost" size="sm"
                    onClick={clearAthletes}
                  >Clear</Button>
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1"
                   data-testid="bulk-athletes-list">
                {filteredAthletes.map(a => (
                  <label
                    key={a.id}
                    className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-900 rounded cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={form.athlete_ids.includes(a.id)}
                      onCheckedChange={() => toggleAthlete(a.id)}
                      data-testid={`bulk-athlete-cb-${a.id}`}
                    />
                    <span className="flex-1">{a.full_name}</span>
                    {a.squad && <span className="text-xs text-gray-500">{a.squad}</span>}
                  </label>
                ))}
                {filteredAthletes.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No athletes match.</p>
                )}
              </div>
            </div>

            <div>
              <Label>Tests * ({form.test_types.length} selected)</Label>
              <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3"
                   data-testid="bulk-tests-list">
                {canonicalCategories.map(([cat, data]) => (
                  <div key={cat}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{cat}</p>
                    <div className="grid grid-cols-2 gap-1">
                      {(data.tests || []).map(t => (
                        <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={form.test_types.includes(t)}
                            onCheckedChange={() => toggleTest(t)}
                            data-testid={`bulk-test-cb-${t}`}
                          />
                          <span>{t}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                data-testid="bulk-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createSession} className="bg-pfap-green hover:bg-pfap-green/90"
                    data-testid="bulk-create-submit">
              Create session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default BulkAssessmentsList;
